import { FeatureToggles } from '@/features/feature-toggles'
import { once } from 'lodash-es'
import { action, computed, observable, reaction } from 'mobx'
import { task } from 'mobx-task'
import { distinctUntilChanged, EMPTY, type Observable, switchMap } from 'rxjs'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { onAbort } from '@/utils/abort-util'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { type TableSortOrder } from '@/components/Table'
import { type Member } from '@/features/iam/members/Member'
import type {
  ListUserTeamMembersFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetTeamMemberFn,
  GetUserDataFn,
  GetWorkspaceSelectionFn,
} from '@/features/iam'
import { type Storage } from '@/infra/storage'
import {
  DefaultLastWorkspaceStorage,
  ALL_WORKSPACES_SELECTION,
} from '@/features/iam/workspaces/LastWorkspaceStorage'
import { type JobSearchViewModel } from '@/features/jobs/list/JobSearchViewModel'
import { type JobsTableColumnsViewModel } from '@/features/jobs/list/JobsTableColumnsViewModel'
import { type JobListPageViewModel } from '@/features/jobs/list/JobListPageViewModel'
import {
  type MutableColumnDetails,
  type StaticColumnDetails,
} from '@/features/jobs/list/jobs-table-columns'
import { type HandleOfferResponseFn } from '@/features/opportunities'
import { type AutomatedAction } from '@/features/opportunities/offers/OpportunityOffersViewModel'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type JobTicketStore } from '@/features/jobs/tickets/JobTicketStore'
import { tapAction, toStream } from '@/utils/mobx-util'
import { type Id } from '@/infra/id'
import { type UpcomingConsultationPreviewViewModel } from '@/features/jobs/list/UpcomingConsultationPreviewViewModel'
import { type NotifyProsOfUnclaimedJobsDialogViewModel } from '@/features/jobs/list/components/JobManagement/NotifyProsOfUnclaimedJobsDialog/NotifyProsOfUnclaimedJobsDialogViewModel'
import { type JobManagementSelectionViewModel } from '@/features/jobs/list/JobManagementSelectionViewModel'

/**
 * Options for {@link JobManagementViewModel}.
 */
export interface JobManagementViewModelOptions {
  /**
   * Whether the view shows the My Jobs list page, whose saved tab must be
   * picked before the first search.
   */
  managesListPage: boolean

  /**
   * Whether the view shows the upcoming consultations preview.
   */
  managesConsultationPreview: boolean

  /**
   * Whether the search, its loaded pages and realtime updates outlive the
   * view, so returning to it shows the list as it was left.
   */
  retainsSearch: boolean
}

/**
 * Job Management view model.
 */
export class JobManagementViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Storage for the last selected workspace(s).
   */
  private readonly _lastWorkspaceStorage

  private readonly _globalSnapshotKey = 'globalWorkspaceSnapshot.jobManagement'

  /**
   * Whether the workspaces filter follows the current workspace, decided on
   * each load.
   */
  @observable private accessor _followCurrentWorkspace = false

  /**
   * Update workspaces filter when current workspace is changed.
   */
  private readonly _workspaceSelectorUpdates$

  constructor(
    readonly searcher: JobSearchViewModel,
    readonly columnsVm: JobsTableColumnsViewModel,
    readonly jobListPageVm: JobListPageViewModel,
    readonly selectionVm: JobManagementSelectionViewModel,
    readonly upcomingConsultationPreviewVm: UpcomingConsultationPreviewViewModel,
    readonly notifyProsOfUnclaimedJobsVm: NotifyProsOfUnclaimedJobsDialogViewModel,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _listUserTeamMembers: ListUserTeamMembersFn,
    currentMember$: Observable<Member>,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _jobTicketStore: JobTicketStore,
    private readonly _localStorage: Storage,
    private readonly _getUserData: GetUserDataFn,
    private readonly _handleOfferResponse: HandleOfferResponseFn,
    private readonly _getWorkspaceSelection: GetWorkspaceSelectionFn,
    private readonly _getTeamMember: GetTeamMemberFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    options: JobManagementViewModelOptions,
  ) {
    this._lastWorkspaceStorage = new DefaultLastWorkspaceStorage(
      this._localStorage,
      'jobManagement',
    )

    this._workspaceSelectorUpdates$ = currentMember$.pipe(
      distinctUntilChanged(),
      tapAction((member) => {
        if (
          this.searcher.workspacesFilter.options.some(
            (o) => o.value === member.workspace.id,
          ) &&
          !this._getWorkspaceSelection().allWorkspaces
        ) {
          this.searcher.workspacesFilter.change([member.workspace.id])
        }
      }),
    )

    // The list page picks its saved tab, which must happen before the
    // searcher activates and runs the first search.
    if (options.managesListPage) {
      this._lifecycle.manage(this.jobListPageVm)
    }
    if (options.managesConsultationPreview) {
      this._lifecycle.manage(this.upcomingConsultationPreviewVm)
    }

    this._lifecycle
      .manage(
        options.retainsSearch
          ? { activate: once(() => this.searcher.activate()) }
          : this.searcher,
      )
      .manage(this.columnsVm)
      .manage(this.selectionVm)
      .manage(
        toStream(() => this._followCurrentWorkspace, true).pipe(
          switchMap((follow) =>
            follow ? this._workspaceSelectorUpdates$ : EMPTY,
          ),
        ),
      )
  }

  /**
   * Whether the current member needs an active PTIN.
   */
  @computed
  get ptinRequired(): boolean {
    const featureEnabled = FeatureToggles.requireActivePtin()
    if (!featureEnabled) {
      return false
    }

    const userData = this._getUserData(this._getCurrentMember().user.publicId)

    return (
      userData?.userDataType === 'Pro' && userData?.ptinStatus === 'Inactive'
    )
  }

  /**
   * Jobs returned by the searcher.
   */
  @computed
  get filteredJobs() {
    return this.searcher.jobs
  }

  /**
   * Whether we are showing the empty job view
   */
  @computed
  get showEmptyView() {
    return (
      this.filteredJobs.length === 0 &&
      !this.searcher.scroller.fetch.pending &&
      this.searcher.scroller.isDone
    )
  }

  /**
   * The workspaces the user has selected.
   */
  @computed
  get selectedWorkspaces() {
    return this.searcher.selectedWorkspaceIds
  }

  /**
   * Whether the member may notify pros about the unclaimed jobs of the one
   * selected workspace.
   */
  @computed
  get canNotifyProsOfUnclaimedJobs() {
    if (this.selectedWorkspaces.length !== 1) {
      return false
    }

    const workspaceId = this.selectedWorkspaces[0]
    const membership = this._getCurrentUserWorkspaceMemberships().find(
      (m) => m.workspace.id === workspaceId,
    )

    return membership?.member.permissions.adminJobUpdate ?? false
  }

  /**
   * The filter that switches between active and archived jobs.
   */
  @computed
  get archivedJobsFilter() {
    return this.searcher.archivedJobsFilter
  }

  /**
   * Shows whether the jobs are active or achived
   */
  @computed
  get jobViewingActionsLabel() {
    return !this.searcher.filters.getFilter('archived')?.value
      ? 'Active jobs'
      : 'Inactive jobs'
  }

  /**
   * Whether the current user may see the job's total.
   */
  canViewJobTotal(job: SearchableJob) {
    const currentMember = this._getCurrentMember(job.workspace.id)
    const isClientCollaborator = job.clientMembers.some(
      (member) => member.isCurrentUser,
    )
    const onOwnerTeamWithViewPricing = Boolean(
      job.clientTeamId &&
      this._getTeamMember(job.clientTeamId, currentMember.user.publicId)
        ?.permissions.viewJobPricing,
    )

    return (
      (isClientCollaborator && currentMember.permissions.viewJobPricing) ||
      onOwnerTeamWithViewPricing ||
      currentMember.permissions.adminViewPricing
    )
  }

  /**
   * Gets the sort order for the specified column.
   */
  getSortOrder(
    column: StaticColumnDetails & MutableColumnDetails,
  ): TableSortOrder {
    return this.searcher.sorting.type === 'Field' &&
      this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   */
  setSortOrder(
    column: StaticColumnDetails & MutableColumnDetails,
    direction: TableSortOrder,
  ) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  /**
   * Shows or hides the selection checkboxes, clearing the selection when
   * hiding them.
   */
  @action.bound
  toggleSelecting() {
    if (this.jobListPageVm.isSelecting) {
      this.selectionVm.clearSelectedJobsForActions()
    }
    this.jobListPageVm.toggleSelecting()
  }

  /**
   * Accepts or declines the offer named by `acceptOffer` / `declineOffer` in
   * the query string. Returns the query string without them, or `null` when
   * there was no offer to respond to.
   */
  respondToOfferInQuery(search: string): string | null {
    const qs = new URLSearchParams(search)
    const action = qsToAcceptDecline(qs)
    if (!action) {
      return null
    }

    this._handleOfferResponse(action)
      .catch(this._createAlertErrorHandler('Responding to the offer failed'))
      .catch(() => undefined)
    qs.delete('acceptOffer')
    qs.delete('declineOffer')
    return qs.toString()
  }

  /**
   * Opens the tab shared in the query string. Returns the query string
   * without it, or `null` when nothing was shared.
   */
  openSharedTabInQuery(search: string): string | null {
    return this.jobListPageVm.tabsVm.openSharedInQuery(search)
  }

  /**
   * Gets the latest open ticket for the specified job.
   */
  getLatestOpenTicketForJob(job: SearchableJob) {
    return this._jobTicketStore.getLatestTicketForJob(job.id)
  }

  /**
   * Sets the table to only show jobs that are active (not archived).
   */
  @action.bound
  viewOnlyActiveJobs() {
    this.archivedJobsFilter.setValue(false)
  }

  /**
   * Sets the table to only show jobs that are archived.
   */
  @action.bound
  viewOnlyArchivedJobs() {
    this.archivedJobsFilter.setValue(true)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch().catch(() => undefined)
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    this._lifecycle.deactivate()
    this._followCurrentWorkspace = false
    if (this.jobListPageVm.isSelecting) {
      this.toggleSelecting()
    }
  }

  /**
   * Loads the team members and restores the workspace filter.
   */
  fetch = task(async () => {
    const signal = this._lifecycle.signal
    await this._listUserTeamMembers(undefined, signal)

    if (!this.searcher.isUserTypePro) {
      const userId = this._getCurrentMember().user.publicId
      const savedWorkspaces = userId
        ? this._lastWorkspaceStorage.load(userId)
        : null

      // Always activate filter to enable persistence
      await this.searcher.workspacesFilter.activate()

      // Determine initial filter state
      // If global "default to all workspaces" is enabled, always use "All workspaces"
      // This takes priority over localStorage to ensure consistent behavior on page load
      const currentGlobalId = this._getWorkspaceSelection().workspace?.id
      const savedGlobalSnapshot = this._localStorage.getItem(
        this._globalSnapshotKey,
      )
      const globalChanged =
        currentGlobalId && savedGlobalSnapshot !== currentGlobalId

      if (this._getWorkspaceSelection().allWorkspaces) {
        this.searcher.workspacesFilter.change([])
      } else if (globalChanged) {
        // Global workspace changed since last visit — sync to it
        this._startFollowingCurrentWorkspace()
      } else if (
        savedWorkspaces &&
        savedWorkspaces !== ALL_WORKSPACES_SELECTION
      ) {
        // Global hasn't changed — restore per-page override from localStorage
        try {
          const workspaceIds = JSON.parse(savedWorkspaces) as string[]
          const validIds = workspaceIds.filter((id) =>
            this.searcher.workspacesFilter.options.some((o) => o.value === id),
          )
          if (validIds.length > 0) {
            this.searcher.workspacesFilter.change(validIds as Id[])
          } else {
            this._startFollowingCurrentWorkspace()
          }
        } catch {
          this._startFollowingCurrentWorkspace()
        }
      } else if (savedWorkspaces === ALL_WORKSPACES_SELECTION) {
        // "All workspaces" was explicitly saved previously
        this.searcher.workspacesFilter.change([])
      } else {
        // No saved selection, use global workspace
        this._startFollowingCurrentWorkspace()
      }

      // Save workspace changes (only when isDefaultToAllWorkspaces is disabled)
      if (userId) {
        onAbort(
          signal,
          reaction(
            () => this.searcher.workspacesFilter.selected.slice(),
            (selected) => {
              // Don't save if isDefaultToAllWorkspaces is enabled
              // This ensures the page always resets to "All workspaces" on next load
              if (this._getWorkspaceSelection().allWorkspaces) {
                return
              }
              if (selected.length === 0) {
                this._lastWorkspaceStorage.save(
                  ALL_WORKSPACES_SELECTION,
                  userId,
                )
              } else {
                this._lastWorkspaceStorage.save(
                  JSON.stringify(selected),
                  userId,
                )
              }
              // Snapshot the current global workspace so we can detect changes on next visit
              const globalId = this._getWorkspaceSelection().workspace?.id
              if (globalId) {
                this._localStorage.setItem(this._globalSnapshotKey, globalId)
              }
            },
            { fireImmediately: false },
          ),
        )

        // React to global workspace selector changes
        onAbort(
          signal,
          reaction(
            () => this._getWorkspaceSelection().workspace?.id,
            (workspaceId) => {
              if (
                workspaceId &&
                this.searcher.workspacesFilter.options.some(
                  (o) => o.value === workspaceId,
                )
              ) {
                this.searcher.workspacesFilter.change([workspaceId as Id])
              }
            },
            { fireImmediately: false },
          ),
        )
      }
    }
  })

  @action.bound
  private _startFollowingCurrentWorkspace() {
    this._followCurrentWorkspace = true
  }
}

/**
 * Maps the query parameter to a well-typed "Automated Action" union.
 */
function qsToAcceptDecline(qs: URLSearchParams): AutomatedAction | null {
  if (qs.has('acceptOffer')) {
    return { type: 'Accept', id: qs.get('acceptOffer')! }
  } else if (qs.has('declineOffer')) {
    return { type: 'Decline', id: qs.get('declineOffer')! }
  }

  return null
}
